import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const PROGRAMS = ["Maker Hub / Maker Village", "Atal EIC"];

const EMPTY = {
  program: "", startup_name: "", founder_name: "", email: "", phone: "", website: "",
  sector: "", stage: "", team_size: "", description: "", support_needed: "", consent: false,
};

const inputCls = "w-full bg-white border border-ink/15 focus:border-teal outline-none px-4 py-3 text-ink text-sm";

export default function IncubationForm() {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState("");

  const set = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.program) { toast.error(t("incubate.program_required")); return; }
    if (!form.consent) { toast.error(t("incubate.consent_required")); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, team_size: form.team_size === "" ? null : Number(form.team_size) };
      const r = await api.post("/incubation/applications", payload);
      setReference(r.data.reference);
      toast.success(t("incubate.success", { ref: r.data.reference }));
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("incubate.fail"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-8" data-testid="incubation-form" aria-label={t("incubate.form_title")}>
      {reference && (
        <div data-testid="incubation-success" role="status" className="border border-teal bg-teal-light text-teal-dark p-4 text-sm">
          {t("incubate.success", { ref: reference })}
        </div>
      )}

      <fieldset className="space-y-4">
        <legend className="tag mb-2">{t("incubate.sec_programme")}</legend>
        <Field label={t("incubate.program")}>
          <div className="grid sm:grid-cols-2 gap-2" role="radiogroup" aria-label={t("incubate.program")}>
            {PROGRAMS.map((p, i) => (
              <label key={p} htmlFor={`inc-program-${i + 1}`}
                className={`border px-4 py-3 text-sm cursor-pointer transition-colors ${form.program === p ? "border-teal bg-teal-light text-teal-dark" : "border-ink/15 text-ink/80 hover:border-teal"}`}>
                <input type="radio" name="program" id={`inc-program-${i + 1}`} data-testid={`inc-program-${i + 1}`}
                  value={p} checked={form.program === p} onChange={set("program")} className="sr-only" />
                {i === 0 ? t("incubate.program_makerhub") : t("incubate.program_atal")}
              </label>
            ))}
          </div>
        </Field>
      </fieldset>

      <fieldset className="space-y-4">
        <legend className="tag mb-2">{t("incubate.sec_applicant")}</legend>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t("incubate.startup_name")}><input data-testid="inc-startup-name" required value={form.startup_name} onChange={set("startup_name")} className={inputCls} /></Field>
          <Field label={t("incubate.founder_name")}><input data-testid="inc-founder-name" required value={form.founder_name} onChange={set("founder_name")} className={inputCls} /></Field>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label={t("incubate.email")}><input data-testid="inc-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} /></Field>
          <Field label={t("incubate.phone")}><input data-testid="inc-phone" value={form.phone} onChange={set("phone")} className={inputCls} /></Field>
          <Field label={t("incubate.website")}><input data-testid="inc-website" value={form.website} onChange={set("website")} className={inputCls} placeholder="https://" /></Field>
        </div>
      </fieldset>

      <fieldset className="space-y-4">
        <legend className="tag mb-2">{t("incubate.sec_venture")}</legend>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label={t("incubate.sector")}><input data-testid="inc-sector" value={form.sector} onChange={set("sector")} className={inputCls} /></Field>
          <Field label={t("incubate.stage")}><input data-testid="inc-stage" value={form.stage} onChange={set("stage")} className={inputCls} placeholder={t("incubate.stage_hint")} /></Field>
          <Field label={t("incubate.team_size")}><input data-testid="inc-team-size" type="number" min="1" value={form.team_size} onChange={set("team_size")} className={inputCls} /></Field>
        </div>
        <Field label={t("incubate.description")}><textarea data-testid="inc-description" required rows={4} value={form.description} onChange={set("description")} className={`${inputCls} resize-none`} /></Field>
        <Field label={t("incubate.support_needed")}><textarea data-testid="inc-support" rows={3} value={form.support_needed} onChange={set("support_needed")} className={`${inputCls} resize-none`} /></Field>
      </fieldset>

      <label className="flex items-start gap-3 border border-amber/60 bg-amber/10 p-4 cursor-pointer">
        <input type="checkbox" data-testid="inc-consent" checked={form.consent} onChange={set("consent")} className="mt-1 accent-[#00886A] w-4 h-4" />
        <span className="text-sm text-ink/85 leading-relaxed">{t("incubate.consent")}</span>
      </label>

      <button type="submit" data-testid="inc-submit" disabled={submitting} className="w-full bg-brand hover:bg-brand-dark disabled:opacity-50 text-white py-4 font-medium transition-colors">
        {submitting ? t("incubate.submitting") : t("incubate.submit")}
      </button>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-mono uppercase tracking-widest text-ink/60 mb-2">{label}</span>
      {children}
    </label>
  );
}
